<template>
  <SlotWrapper>
    <template #body>
      <div>
        <ServiceCategoryForm v-model="formData.service_category_id" />
        <Divider />
        <DurationForm
          v-model="formData.duration_minutes"
          :appointment="appointment"
          :selected-service-category="selectedServiceCategory"
          :start-time="startTime"
          @clear="resetDuration"
        />
        <Divider />
        <EligibleTechniciansForm
          v-model="formData.user_id"
          :service-category-id="formData.service_category_id"
          :pinned-technician-id="pinnedTechnicianId"
          :pinned-reason="originalValues.pinned_reason"
          @update-pinned-reason="setPinnedReason"
        />
        <Divider />
        <AvailabilityForm
          :scheduled-time="appointment.scheduled_time"
          :arrival_window_start="appointment.arrival_window_start"
          :arrival_window_end="appointment.arrival_window_end"
          data-vv-name="Scheduled"
          :service-category-id="formData.service_category_id"
          :technician-id="primaryTechId"
          :technician-ids="multiTechUserIds"
          :duration-minutes="formData.duration_minutes"
          :timezone="appointment.address.time_zone"
          :postal-code="appointment.address.zip_code"
          :current-scheduled-time="appointment.scheduled_time"
          allow-do-not-notify
          @do-not-notify="setDoNotNotify"
          @update:data="updateData"
        />
        <ChangeSummary :original="originalValues" :changes="changes" />
      </div>
    </template>
  </SlotWrapper>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { getObjDiff, getProperty } from "@/lib/helpers.js";

import Divider from "@/components/Divider.vue";
import SlotWrapper from "@/components/SlotWrapper";
import { useMultiTechSelect } from "@/composables/useMultiTechSelect";
import { AppointmentServiceCategory } from "@/typings/appointment";
import useVuelidate from "@vuelidate/core";
import ChangeSummary from "../../ChangeSummary.vue";
import AvailabilityForm from "../../forms/AvailabilityForm.vue";
import DurationForm from "../../forms/DurationForm.vue";
import EligibleTechniciansForm from "../../forms/EligibleTechniciansForm.vue";
import ServiceCategoryForm from "../../forms/ServiceCategoryForm.vue";

const props = defineProps<{
  appointment: Record<string, any>;
}>();

const emit = defineEmits<{
  "changes-update": [value: Record<string, any>];
}>();

const store = useStore();
const { isMultiTechEnabled, toTechProfileIds } = useMultiTechSelect();

// Vuelidate instance for parent cascading validation
useVuelidate();

const formData = ref<Record<string, any>>({});

// --- Computed ---

const selectedServiceCategory = computed<
  AppointmentServiceCategory | undefined
>(() => {
  return store.state.serviceCategories.find(
    (sc: AppointmentServiceCategory) =>
      sc.id === formData.value.service_category_id
  );
});

const pinnedTechnicianId = computed(() => {
  const { assignment_pinned, technician } = props.appointment;
  return (assignment_pinned && technician && technician.user_id) || null;
});

/**
 * Extract user_ids from appointment_technicians sorted by position.
 * Cross-references with appointment.technicians to resolve user_id.
 * API sends technician_id (profile id) on each technician; support both
 * technician_id and technician_profile_id for matching.
 */
function getMultiTechUserIds(): number[] {
  const { appointment_technicians, technicians } = props.appointment;
  if (!appointment_technicians?.length || !technicians?.length) return [];

  const sorted = [...appointment_technicians].sort(
    (a: any, b: any) => a.position - b.position
  );
  return sorted
    .map((at: any) => {
      const profileId = at.technician_profile_id;
      const tech = technicians.find(
        (t: any) =>
          t.technician_profile_id === profileId || t.technician_id === profileId
      );
      return tech?.user_id;
    })
    .filter((id: number | undefined): id is number => id != null);
}

function getInitialUserIds(): number | number[] | null {
  if (isMultiTechEnabled.value) {
    const multiTechIds = getMultiTechUserIds();
    if (multiTechIds.length > 0) return multiTechIds;
  }
  const { assignment_pinned, technician } = props.appointment;
  return assignment_pinned ? getProperty(technician, "user_id") : null;
}

const originalValues = computed(() => {
  const { service_category, estimated_end_time, pinned_reason } =
    props.appointment;
  return {
    ...props.appointment,
    pinned_reason,
    estimated_end_time: moment(estimated_end_time).format("h:mm a"),
    service_category_id: getProperty(service_category, "id"),
    user_id: getInitialUserIds(),
    skip_notifications: false
  };
});

function arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((val, i) => val === b[i]);
}

const changes = computed(() => {
  if (Object.keys(formData.value).length === 0) return {};
  const diff = getObjDiff(originalValues.value, formData.value);

  // getObjDiff uses !== which always differs for arrays by reference
  if (
    diff.user_id &&
    Array.isArray(diff.user_id) &&
    Array.isArray(originalValues.value.user_id) &&
    arraysEqual(originalValues.value.user_id, diff.user_id)
  ) {
    delete diff.user_id;
  }

  // Don't show estimated end time changes when rescheduling
  if (!Object.prototype.hasOwnProperty.call(diff, "duration_minutes")) {
    delete diff.estimated_end_time;
  }
  return diff;
});

const primaryTechId = computed(() => {
  const uid = formData.value.user_id;
  if (Array.isArray(uid)) return uid[0] ?? null;
  return uid;
});

const multiTechUserIds = computed<number[] | undefined>(() => {
  if (!isMultiTechEnabled.value) return undefined;
  const uid = formData.value.user_id;
  if (Array.isArray(uid)) return uid;
  if (uid != null) return [uid];
  return undefined;
});

const startTime = computed(() => {
  const { dispatch_time, min_arrival_time, arrival_window_minutes } =
    props.appointment;
  const { scheduled_time } = formData.value;

  // If availability has been changed, use new scheduled time
  // else use actual dispatch time or min arrival time
  return scheduled_time
    ? moment(scheduled_time)
        .subtract(arrival_window_minutes, "minutes")
        .format()
    : moment(dispatch_time || min_arrival_time).format();
});

// --- Methods ---

function setFormData() {
  if (!props.appointment) return;
  const {
    duration_minutes,
    service_category,
    scheduled_time,
    arrival_window_start,
    arrival_window_end,
    estimated_end_time,
    pinned_reason
  } = props.appointment;

  formData.value = {
    service_category_id: getProperty(service_category, "id"),
    duration_minutes,
    user_id: getInitialUserIds(),
    scheduled_time,
    arrival_window_start,
    arrival_window_end,
    estimated_end_time: moment(estimated_end_time).format("h:mm a"),
    pinned_reason,
    skip_notifications: false
  };
}

function resetDuration() {
  const { duration_minutes } = originalValues.value;
  formData.value.duration_minutes =
    selectedServiceCategory.value && duration_minutes
      ? selectedServiceCategory.value.duration_minutes
      : duration_minutes;
}

function setEndTime() {
  const {
    dispatch_time,
    min_arrival_time,
    arrival_window_minutes,
    estimated_duration_minutes
  } = props.appointment;
  const { scheduled_time, duration_minutes } = formData.value;

  const start = scheduled_time
    ? moment(scheduled_time).subtract(arrival_window_minutes, "minutes")
    : moment(dispatch_time || min_arrival_time);

  formData.value.estimated_end_time = moment(start)
    .add(duration_minutes || estimated_duration_minutes, "minutes")
    .format("h:mm a");
}

function setPinnedReason(val: string) {
  formData.value.pinned_reason = val;
}

function setDoNotNotify(val: boolean) {
  formData.value.skip_notifications = val;
}

function updateData({ key, value }: { key: string; value: any }) {
  formData.value[key] = value;
}

function getChanges(): Record<string, any> {
  const result = { ...changes.value };

  if (
    isMultiTechEnabled.value &&
    Object.prototype.hasOwnProperty.call(result, "user_id")
  ) {
    const uid = result.user_id;
    const ids = Array.isArray(uid) ? uid : uid != null ? [uid] : [];
    result.technician_profile_ids = toTechProfileIds(ids);
    delete result.user_id;
  }

  return result;
}

// --- Watchers ---

watch(
  () => formData.value.service_category_id,
  (newValue, oldValue) => {
    if (newValue !== oldValue && oldValue) {
      if (selectedServiceCategory.value) {
        formData.value.duration_minutes =
          selectedServiceCategory.value.duration_minutes;
      }
    }
  }
);

watch(
  () => formData.value.duration_minutes,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      setEndTime();
    }
  }
);

watch(changes, (newValue) => {
  emit("changes-update", newValue);
});

// --- Init (equivalent to created()) ---
setFormData();

// Expose getChanges for parent ref access (SchedulingModule.vue)
defineExpose({ getChanges });
</script>

<style lang="scss" scoped></style>
